// A03: per-workspace autonomy toggle inside Settings. Three levels —
// freeform, paused_on_commit (default), paused_on_destructive. The
// working agent reads workspace.autonomy_level on every turn.

import { useEffect, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "../../spacetimedb-bindings";
import {
  ApiError,
  setWorkspaceAutonomy,
  type AutonomyLevel,
} from "../api/client";

interface Props {
  workspaceId: number;
}

interface Option {
  id: AutonomyLevel;
  label: string;
  hint: string;
}

const OPTIONS: Option[] = [
  {
    id: "freeform",
    label: "Freeform",
    hint: "Working agent runs uninterrupted. Best for trusted personas on mature workspaces.",
  },
  {
    id: "paused_on_commit",
    label: "Paused on commit",
    hint: "Agent pauses before each commit for your approval. Safest default.",
  },
  {
    id: "paused_on_destructive",
    label: "Paused on destructive",
    hint: "Agent pauses before rm, force-push, and schema migrations. Commits run freely.",
  },
];

export function AutonomyPanel({ workspaceId }: Props) {
  const [rows] = useTable(tables.workspace);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const current: AutonomyLevel =
    (rows.find((w) => Number(w.workspaceId) === workspaceId)
      ?.autonomyLevel as AutonomyLevel | undefined) ?? "paused_on_commit";

  const [pending, setPending] = useState<AutonomyLevel>(current);
  useEffect(() => setPending(current), [current]);

  async function pick(level: AutonomyLevel) {
    if (busy || level === current) return;
    setPending(level);
    setBusy(true);
    setError("");
    try {
      await setWorkspaceAutonomy(workspaceId, level);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not update autonomy level",
      );
      setPending(current);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="autonomy-panel"
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">Autonomy</h3>
        <p className="mt-1 text-xs text-muted">
          How much autonomy the working agent has in this workspace. Applies
          at the next turn.
        </p>
      </header>
      <div className="flex flex-col gap-2">
        {OPTIONS.map((opt) => {
          const selected = pending === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              disabled={busy}
              onClick={() => void pick(opt.id)}
              className={`cursor-pointer rounded-md border px-3 py-2 text-left transition-colors ${
                selected
                  ? "border-accent bg-surface-raised"
                  : "border-edge hover:border-accent"
              } disabled:cursor-not-allowed disabled:opacity-50`}
              data-testid={`autonomy-${opt.id}`}
            >
              <p className="text-xs font-medium text-primary">{opt.label}</p>
              <p className="mt-1 text-[0.6875rem] text-muted">{opt.hint}</p>
            </button>
          );
        })}
      </div>
      {error && (
        <p className="text-xs text-danger" data-testid="autonomy-error">
          {error}
        </p>
      )}
    </section>
  );
}
